import classNames from 'classnames';
import React, {
  useRef,
  useState,
  useEffect,
  JSX,
  useCallback,
  useMemo,
  RefObject,
  FunctionComponent,
  Activity,
} from 'react';

import FileInput from './file-input';
import { fileType } from './uploads.types';
import toastTrigger from '@ui/toast/toast-trigger';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import OptionalComponent from '@ui/optional-component/optional-component';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import GradientButton from '@ui/buttons/gradient-button/gradient-button';
import { EColorGradients } from '@/types/color-gradients.types';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import FolderPaths from '@/utils/images';
import Modal from '@ui/modal/modal';
import ImageCropper from '@ui/image-cropper/image-cropper';
import { InitialAspectRatioOptionsEnum } from '@/types/images.types';
import Container from '@ui/container/container';
import DeleteModal from './delete-modal';
import { mergeRefs } from '@/utils/merge-refs';
import useContrastChecker from '@/utils/use-contrast-checker/use-contrast-checker';

import styles from './upload-large.module.css';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

interface ILargeButtonProps {
  disabled?: boolean;
  title?: string;
  subtitle?: string;
  onClick?: () => void;
  onDelete?: () => void;
  onFile?: (e: File | FileList) => void;
  multiple?: boolean;
  maxFileCount?: number;
  allowedFileTypes: fileType[];
  style?: React.CSSProperties;
  uploading?: boolean;
  errorMessage?: string;
  errorOutline?: boolean;
  CustomTitle?:
    | JSX.Element
    | string
    | FunctionComponent<{ hasValidContrast: { hasValidContrast: boolean; backgroundMode: 'light' | 'dark' } | null }>;
  /** validate the file extension instead of the file content. This can be used when you need to upload a certain file extention but not all files with that extensions type. For example: scss files have type application/octet-stream, but we dont want to allow all application/octet-stream files, so we just allow .css extensions. */
  allowedFileExtensions?: string[];
  id?: string;
  fileMetadataCallback?: (fileName: string, fileSize: string) => void;
  label?: string;
  maxSizeInBytes?: number;
  description?: string;
  hideText?: boolean;
  displayImageUrl?: string | null;
  standard?: boolean;
  wrapperClassNames?: string;
  hideIconIfImage?: boolean;
  hidePlaceholderImage?: boolean;
  editable?: boolean;
  deletable?: boolean;
  placeholder?: string;
  disableEditingAspectRatio?: boolean;
  initialAspectRatio?: InitialAspectRatioOptionsEnum;
  requireDeleteModal?: boolean;
  ref?: RefObject<HTMLInputElement | null>;
  noGradients?: boolean;
  isFocusable?: boolean;
  EditActionsPreHoverContent?: FunctionComponent<{
    hasValidContrast: { hasValidContrast: boolean; backgroundMode: 'light' | 'dark' };
  }> | null;
  setImageColorState?: (state: 'light' | 'light' | 'dark') => void;
  actionButtonsType?: 'cover' | 'default';
  onCropModalToggle?: (open: boolean) => void;
  contrastRegionCheck?: {
    foregroundColor: string;
    sampleRegion: { x: number; y: number; width: number; height: number; units: 'px' | 'pct' };
    sampleStep?: number;
  };
  /** overrides the button hover background color for standard uploads only */
  buttonHoverBackgroundColor?: string;
  /** overrides the button border radius for standard uploads only */
  buttonBorderRadius?: string;
  noShadow?: boolean;
}
interface INonUploadLargeButtonProps extends Omit<ILargeButtonProps, 'allowedFileTypes' | 'onFile'> {
  /** if LargeButton is not used to upload files, you must include this prop. Otherwise you are forced to include allowedFileTypes */
  nonUpload: boolean;
}

function bytesToString(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }

  return `${bytes} B`;
}

const UploadLarge = (props: ILargeButtonProps | INonUploadLargeButtonProps) => {
  const {
    title,
    subtitle,
    disabled,
    onClick,
    onDelete,
    multiple,
    maxFileCount,
    style = {},
    uploading,
    errorMessage,
    errorOutline,
    displayImageUrl,
    CustomTitle,
    allowedFileExtensions = [],
    id,
    fileMetadataCallback,
    label,
    maxSizeInBytes: maxSize,
    description,
    hideText = false,
    standard = true,
    wrapperClassNames,
    hideIconIfImage,
    hidePlaceholderImage,
    editable,
    deletable,
    placeholder = hidePlaceholderImage ? undefined : FolderPaths.ImageUploadPlaceholder,
    disableEditingAspectRatio,
    initialAspectRatio,
    requireDeleteModal,
    ref,
    noGradients = false,
    isFocusable = true,
    EditActionsPreHoverContent,
    setImageColorState,
    actionButtonsType = 'default',
    onCropModalToggle,
    contrastRegionCheck,
    buttonHoverBackgroundColor,
    buttonBorderRadius,
    noShadow = false,
  } = props;

  const allowedFileTypes = useMemo(() => {
    let _allowedFileTypes: fileType[] | undefined = [];
    if ((props as ILargeButtonProps)?.allowedFileTypes) {
      _allowedFileTypes = (props as ILargeButtonProps).allowedFileTypes;
    }

    return _allowedFileTypes;
  }, [props]);

  let onFile: ILargeButtonProps['onFile'] | undefined;
  if ((props as ILargeButtonProps)?.onFile) {
    onFile = (props as ILargeButtonProps).onFile;
  }

  const [draggingOver, setDraggingOver] = useState(false);
  const [showTitle, setShowTitle] = useState(title);
  const [showDescription, setShowDescription] = useState(subtitle);
  const [cropping, setCropping] = useState<boolean>(false);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const baseLabelRGB = '255, 255, 255';
  const baseLabelOpacity = '0.05';

  const _contrastCheckerResults = useContrastChecker({
    ref: buttonRef,
    shouldRun: !!contrastRegionCheck || !!(EditActionsPreHoverContent || (displayImageUrl && (editable || deletable))),
    backgroundImageSrc: displayImageUrl || placeholder || '',
    foregroundColor: contrastRegionCheck?.foregroundColor || `rgba(${baseLabelRGB}, ${baseLabelOpacity})`,
    sampleRegion: contrastRegionCheck?.sampleRegion || { x: 50, y: 2, width: 50, height: 25, units: 'pct' },
    sampleStep: contrastRegionCheck?.sampleStep || 2,
  });

  const contrastCheckerResults = useMemo(() => {
    if (_contrastCheckerResults) return _contrastCheckerResults;

    return { hasValidContrast: true, backgroundMode: 'dark' } as {
      hasValidContrast: boolean;
      backgroundMode: 'light' | 'dark';
    };
  }, [_contrastCheckerResults]);

  useEffect(() => {
    setImageColorState?.(contrastCheckerResults.backgroundMode);
  }, [setImageColorState, contrastCheckerResults]);

  useEffect(() => {
    if (fileMetadataCallback && showTitle && showDescription) {
      fileMetadataCallback(showTitle, showDescription);
    }
  }, [showTitle, showDescription, fileMetadataCallback]);

  // prevents user from hitting save before the current edits have been generated into a file
  const handleEditing = useCallback((inProcess: boolean) => {
    setCropping(inProcess);
  }, []);

  function handleDragOver(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDragEnter(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    if (onFile) {
      setDraggingOver(true);
    }
  }

  function handleDragLeave(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (onFile) {
      setDraggingOver(false);
    }
  }

  function handleDragDrop(e: React.DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    if (onFile) {
      const files = e.dataTransfer?.files;

      if (inputRef.current) {
        // Instead of calling processFiles(files) directly, we want to trigger a change event on the file input so that we can run validation.
        // after validation, processFiles(files) will be called.
        const ev = new Event('change', { bubbles: true });
        inputRef.current.files = files;
        inputRef.current.dispatchEvent(ev);
      }
    }
  }

  const processFiles = useCallback(
    (files: FileList) => {
      const file = files?.[0];

      //if they didn't drop anything, leave it
      if (!files || files.length < 1) {
        return;
      }

      //if we allow multiple files and they've dropped more than one
      if (multiple && files.length > 1) {
        let totalBytes = 0;

        if (maxFileCount && files.length > maxFileCount) {
          return toastTrigger(
            <div>
              <h6>Too many files</h6>
              <p>The total size of all files must be less than {maxFileCount}.</p>
            </div>,
            {
              type: 'error',
              autoClose: 7000,
            }
          );
        }

        //there are multiple, get the size of all of them
        for (let i = 0; i < files.length; ++i) {
          totalBytes += files[i].size;
        }

        if (maxSize && totalBytes > maxSize) {
          return toastTrigger(
            <div>
              <h6>File size too large</h6>
              <p>The total size of all files must be less than {bytesToString(maxSize)}.</p>
            </div>,
            {
              type: 'error',
              autoClose: 7000,
            }
          );
        }

        //show feedback
        setShowTitle(`${files.length} files selected.`);
        setShowDescription(`Total size: ${bytesToString(totalBytes)}`);
        setDraggingOver(false);
        onFile?.(files);

        //return early, skip everything below
        return;
      }

      //if we have just one file
      if (file) {
        if (maxSize && file.size > maxSize) {
          return toastTrigger(
            <div>
              <h6>File size too large</h6>
              <p>The file size must be less than {bytesToString(maxSize)}.</p>
            </div>,
            {
              type: 'error',
              autoClose: 7000,
            }
          );
        }

        setShowTitle(file.name);
        setShowDescription(`Size: ${bytesToString(file.size)}`);
        onFile?.(file);
      }

      setDraggingOver(false);
    },
    [maxSize, multiple, onFile, maxFileCount]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const fileCount = e.target.files.length;
        const maxFilesAllowed = 50;
        if (fileCount > maxFilesAllowed) {
          return toastTrigger(
            `You have selected ${fileCount} files. You may upload a max of ${maxFilesAllowed} files at a time.`,
            {
              type: 'error',
              autoClose: 7000,
            }
          );
        }

        processFiles(e.target.files);
      }
    },
    [processFiles]
  );

  function handleClick() {
    inputRef.current?.click();
  }

  function handleFileError(error: string, file: File) {
    console.error(error);
    setShowTitle(`File ${file.name} is not a valid type.`);
    setShowDescription('Please select a different file.');
  }

  const renderInnerContent = useCallback(() => {
    return (
      <div
        className="flex fd-column jc-center ai-center width-100-percent height-100-percent"
        style={{
          position: 'absolute',
          top: '-0.5px',
          left: '-0.5px',
        }}
      >
        {label && (
          <div>
            <label>{label}</label>
          </div>
        )}
        <OptionalComponent display={!uploading && !hideText && !displayImageUrl}>
          <>
            {CustomTitle && (
              <>
                {typeof CustomTitle === 'function' ? (
                  <CustomTitle hasValidContrast={contrastCheckerResults} />
                ) : (
                  CustomTitle
                )}
              </>
            )}

            {!CustomTitle && (
              <label className={`flex jc-center ai-center ${showTitle ? 'gap-4' : ''}`}>
                {
                  <>
                    {title === showTitle && !hideIconIfImage && <Icon name={ICONS.ADD} size={12} />}
                    <Text tag="span" className="S2" color="var(--gray-300)">
                      {showTitle}
                    </Text>
                  </>
                }
              </label>
            )}
          </>
          <OptionalComponent display={!!description}>
            <div>
              <Text tag="span" className="S2" color="var(--gray-300)">
                {description}
              </Text>
            </div>
          </OptionalComponent>
        </OptionalComponent>
        {subtitle && (
          <div>
            <Text tag="span" className="S2" color="var(--gray-300)">
              {showDescription}
            </Text>
          </div>
        )}
        <FileInput
          disabled={disabled}
          accept={allowedFileTypes}
          ref={mergeRefs(inputRef, ref)}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          onFileError={handleFileError}
          acceptFileExtensions={allowedFileExtensions}
          multiple={multiple}
        />
        {uploading && (
          <div>
            <WaitingIndicator />
          </div>
        )}
        {errorMessage && <label>{errorMessage}</label>}
      </div>
    );
  }, [
    label,
    uploading,
    hideText,
    displayImageUrl,
    CustomTitle,
    showTitle,
    title,
    hideIconIfImage,
    description,
    subtitle,
    showDescription,
    disabled,
    allowedFileTypes,
    ref,
    handleFileChange,
    allowedFileExtensions,
    errorMessage,
    multiple,
    contrastCheckerResults,
  ]);

  const openCropModal = !!displayImageUrl && !!showCropModal;

  useEffect(() => {
    onCropModalToggle?.(openCropModal);
  }, [onCropModalToggle, openCropModal]);

  const renderEditButtons = useCallback(() => {
    if (uploading || (!displayImageUrl && actionButtonsType === 'cover')) return null;

    const displayEditButtons = editable || deletable;

    return (
      <Container
        className={`${styles.editContainer} ${styles[`actionButtonsType-${actionButtonsType}`]} ${EditActionsPreHoverContent ? styles['hasPreHoverContent'] : ''} ${displayEditButtons && !EditActionsPreHoverContent ? styles['editableNoPreHoverContent'] : ''} upload-large-edit-container`}
      >
        <Activity mode={EditActionsPreHoverContent ? 'visible' : 'hidden'}>
          <div
            className={`${styles.editActionsPreHoverContent} ${displayImageUrl && displayEditButtons ? styles['allow-hover-state'] : ''}`}
          >
            {EditActionsPreHoverContent ? (
              <EditActionsPreHoverContent hasValidContrast={contrastCheckerResults} />
            ) : null}
          </div>
        </Activity>

        <Activity mode={displayImageUrl && displayEditButtons ? 'visible' : 'hidden'}>
          <div
            className={`${styles.editActionsPostHoverContent} ${contrastCheckerResults.hasValidContrast ? '' : styles['invalid-contrast']} flex ai-center gap-4`}
          >
            {/* do not use custom Button becaue we have very specific class styles here based on contrast */}
            <OptionalComponent display={editable}>
              <button
                className={`clear-button radius-full square-32 padding-4 flex-column jc-center ai-center overflow-hidden ${styles[`background-is-${contrastCheckerResults.backgroundMode}`]}`}
                onClick={onClick ? onClick : handleClick}
              >
                <Icon name={ICONS.EDIT} size={16} color="var(--global-ico-text-1)" />
              </button>
              <button
                className={`clear-button radius-full square-32 padding-4 flex-column jc-center ai-center overflow-hidden ${styles[`background-is-${contrastCheckerResults.backgroundMode}`]}`}
                onClick={() => setShowCropModal(true)}
              >
                <Icon name={ICONS.IMAGE_CROP} size={16} color="var(--global-ico-text-1)" />
              </button>
            </OptionalComponent>
            <OptionalComponent display={deletable}>
              <button
                className={`clear-button radius-full square-32 padding-4 flex-column jc-center ai-center overflow-hidden ${styles[`background-is-${contrastCheckerResults.backgroundMode}`]}`}
                onClick={() => {
                  if (requireDeleteModal) {
                    setShowDeleteModal(true);
                  } else {
                    onDelete?.();
                  }
                }}
              >
                <Icon name={ICONS.DELETE_TRASH_BIN} size={18} color="var(--global-ico-text-1)" />
              </button>
            </OptionalComponent>
          </div>

          <OptionalComponent display={editable}>
            <Modal
              open={openCropModal}
              onRequestClose={() => setShowCropModal(false)}
              renderHeaderContent={() => (
                <Container height="70px" className="padding-t-24 padding-h-40">
                  <Text tag="h4" className="H4">
                    Edit Image
                  </Text>
                </Container>
              )}
              renderFooterContent={() => (
                <div className="flex jc-flex-end ai-center gap-16 padding-h-40 padding-b-24">
                  <Button
                    onClick={() => {
                      setShowCropModal(false);
                      setCroppedImage(null);
                    }}
                    design={TButtonAndLinkDesigns.LocalBttnSec}
                  >
                    <Text className="L1">Cancel</Text>
                  </Button>
                  <Button
                    hoverOpacity={0.8}
                    disabled={!croppedImage || !!cropping}
                    onClick={() => {
                      if (!croppedImage) return;
                      setShowCropModal(false);
                      onFile?.(croppedImage as File);
                      setCroppedImage(null);
                    }}
                    design={TButtonAndLinkDesigns.LocalBttnPrim}
                    backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
                  >
                    <Text
                      color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                      className="L1"
                    >
                      Save
                    </Text>
                  </Button>
                </div>
              )}
              customSize={{
                width: '640px',
                height: '710px',
                maxHeight: '95vh',
                maxWidth: '95vw',
                minHeight: '300px',
              }}
            >
              <div className="padding-t-16 padding-h-40">
                <ImageCropper
                  originalImage={displayImageUrl || ''}
                  disableEditingAspectRatio={disableEditingAspectRatio}
                  initialAspectRatio={initialAspectRatio}
                  onFinished={(file) => {
                    setCroppedImage(file);
                  }}
                  setCropping={handleEditing}
                />
              </div>
            </Modal>
          </OptionalComponent>
        </Activity>
      </Container>
    );
  }, [
    editable,
    uploading,
    displayImageUrl,
    onClick,
    disableEditingAspectRatio,
    initialAspectRatio,
    handleEditing,
    requireDeleteModal,
    onDelete,
    croppedImage,
    cropping,
    onFile,
    EditActionsPreHoverContent,
    contrastCheckerResults,
    actionButtonsType,
    deletable,
    openCropModal,
  ]);

  if (standard) {
    const hoverAndFocusStates = buttonHoverBackgroundColor
      ? {
          hoverBackgroundColor: buttonHoverBackgroundColor,
          focusBackgroundColor: buttonHoverBackgroundColor,
        }
      : {};
    const borderRadius = buttonBorderRadius
      ? {
          borderRadius: buttonBorderRadius,
        }
      : {};

    return (
      <>
        <div
          className={`position-relative width-100-percent height-100-percent ${styles.buttonContainer}`}
          data-component="UploadLarge"
        >
          <Button
            ref={buttonRef}
            className={classNames(
              styles.button,
              styles.standard,
              'bg-image-cover',
              'radius-16',
              'position-relative',
              'padding-0',
              {
                [styles.dragging]: draggingOver,
                [styles.error]: !!errorOutline,
                [styles.noShadow]: noShadow,
              }
            )}
            height="100%"
            width="100%"
            onClick={onClick ? onClick : handleClick}
            onDragEnter={handleDragEnter}
            onDragOverCapture={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDragDrop}
            onDragEnd={handleDragDrop}
            onDragOver={handleDragOver}
            tabIndex={isFocusable ? 0 : -1}
            style={{
              ...style,
              ...((displayImageUrl || placeholder) && {
                backgroundImage: `url(${displayImageUrl || (noGradients ? undefined : placeholder)})`,
              }),
            }}
            {...hoverAndFocusStates}
            {...borderRadius}
          >
            <div className="position-relative height-100-percent width-100-percent flex jc-center ai-center">
              {renderInnerContent()}
            </div>
          </Button>

          {renderEditButtons()}
        </div>

        <OptionalComponent display={editable && requireDeleteModal}>
          <DeleteModal
            img={displayImageUrl}
            setShowDeleteModal={setShowDeleteModal}
            showDeleteModal={showDeleteModal}
            onDelete={onDelete}
          />
        </OptionalComponent>
      </>
    );
  }

  return (
    <>
      <div
        className={`position-relative width-100-percent height-100-percent ${styles.buttonContainer}`}
        data-component="UploadLarge"
      >
        <GradientButton
          gradient={noGradients ? EColorGradients.NoGradientAtAll : EColorGradients.Sunset}
          defaultBackground={'var(--gray-1100)'}
          selected={true}
          borderWidth={'1px'}
          borderStyle={'dashed'}
          dashedColor={'var(--gray-1100)'}
          wrapperClassNames={classNames(
            noGradients ? undefined : styles.gradientButton,
            styles.button,
            'height-100-percent',
            'radius-8',
            'overflow-hidden',
            wrapperClassNames,
            {
              [styles.dragging]: draggingOver,
              [styles.error]: !!errorOutline,
              [styles.noShadow]: noShadow,
            }
          )}
          buttonClassNames="bg-image-cover"
          loading={uploading}
          buttonProps={{
            ref: buttonRef,
            disabled: uploading || disabled,
            onClick: onClick ? onClick : handleClick,
            onDragEnter: handleDragEnter,
            onDragOverCapture: handleDragEnter,
            onDragLeave: handleDragLeave,
            onDrop: handleDragDrop,
            onDragEnd: handleDragDrop,
            onDragOver: handleDragOver,
            tabIndex: isFocusable ? 0 : -1,
            style: {
              ...style,
              backgroundColor: 'var(--gray-1100)',
              ...((displayImageUrl || placeholder) && {
                backgroundImage: `url(${displayImageUrl || (noGradients ? undefined : placeholder)})`,
              }),
            },
            id: id,
          }}
        >
          {renderInnerContent()}
        </GradientButton>
        {renderEditButtons()}
      </div>
      <OptionalComponent display={editable && requireDeleteModal}>
        <DeleteModal
          img={displayImageUrl}
          setShowDeleteModal={setShowDeleteModal}
          showDeleteModal={showDeleteModal}
          onDelete={onDelete}
        />
      </OptionalComponent>
    </>
  );
};

export default UploadLarge;
